<template>
<div class="filters__inner">
  <div
    v-if="searchable"
    class="filter-row search-row"
  >
    <up-input
      :propValue="search"
      :placeholder="$t('shared.filters.search_placeholder')"
      @onInput="(e) => search = e"
    />
  </div>
  <template
    v-for="filter in filterList"
    :key="filter.name"
  >
    <div
      v-for="level in levelsOf(filter)"
      :key="`${filter.name}-${level.index}`"
      class="filter-row"
    >
      <drop-list
        :options="level.options"
        :selected="level.selected"
        :default-name="level.title"
        :resettable="!level.isChipLevel"
        @update="(value) => selectLevel(filter, level, value)"
      />
    </div>
    <div
      v-if="chipsOf(filter).length"
      :key="`${filter.name}-chips`"
      class="filter-row section-btn-row"
    >
      <button
        v-for="chip of chipsOf(filter)"
        :key="chip.id"
        @click="unselectChip(filter, chip.id)"
        class="up-btn-section"
      >
        <span class="up-btn-section__label">{{ chip.name }}</span>
        <i class="exu-x-close" />
      </button>
    </div>
  </template>
</div>
<div
  class="filter-row btn-row"
>
  <button
    @click="resetFilters"
    class="up-btn-filters"
  >
    {{ $t('shared.filters.btn_reset') }}
  </button>
  <button
    :disabled="!isFilter"
    @click="returnFilters"
    class="up-btn-filters"
  >
    {{ $t('shared.filters.btn_apply') }}
  </button>
</div>
</template>
<script>
export default {
  name: 'filtersView',
  // у компонента два корневых узла, поэтому обработчик надо объявить явно —
  // иначе Vue ругается, что не может его унаследовать
  emits: ['update'],
  props: {
    // конфиг с сервера as is: [{ name, label_ru, label_en, type, values }]
    filters: {
      type: Array,
      default: () => ([]),
    },
    // состояние, возвращённое прошлым `update` — чтобы панель открылась как её закрыли
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    // подписи вложенных уровней: имён уровней конфиг не отдаёт.
    // Ключ — имя фильтра, значение — подписи со второго уровня и глубже.
    levelTitles: {
      type: Object,
      default: () => ({}),
    },
    // Строка поиска внутри панели. С 02.10.2026 её никто не включает:
    // у экспонентов и участников поиск живёт в строке над списком и
    // чистится крестиком в поле, а панель ведает только фильтрами.
    // Проп оставлен на случай каталога без своей строки поиска.
    searchable: {
      type: Boolean,
      default: false,
    },
    searchValue: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      locale: null,
      search: this.searchValue,
      // { [имя фильтра]: { value } | { path: [], items: [] } }
      state: {},
    };
  },
  computed: {
    filterList() {
      if (!Array.isArray(this.filters)) return [];
      return this.filters.filter((filter) => filter
        && filter.name
        && Array.isArray(filter.values)
        && filter.values.length);
    },
    hasSelection() {
      return this.filterList.some((filter) => {
        const state = this.state[filter.name];
        if (!state) return false;
        return this.isMulti(filter) ? !!state.items.length : state.value !== null;
      });
    },
    // «Применить» доступно и когда поиск только что стёрли: это тоже изменение
    isFilter() {
      return this.hasSelection
        || !!this.search
        || (this.search || null) !== (this.searchValue || null);
    },
  },
  mounted() {
    this.locale = this.$store.getters.getLang;
    this.restoreState();
  },
  methods: {
    isMulti(filter) {
      return filter.type === 'multi';
    },
    label(item) {
      return (this.locale === 'en' ? item.label_en : item.label_ru) || item.label_ru || '';
    },
    // элемент — лист, если внутрь него провалиться некуда
    isLeaf(item) {
      return !Array.isArray(item.values) || !item.values.length;
    },
    toOptions(items) {
      return items.map((item, index) => ({
        id: item.id !== undefined && item.id !== null ? item.id : `auto-${index}`,
        name: this.label(item),
        values: item.values,
      }));
    },
    // Разворачивает фильтр в цепочку выпадающих списков: сколько уровней вложенности
    // в конфиге, столько списков. У плоского фильтра уровень один.
    levelsOf(filter) {
      const state = this.state[filter.name];
      if (!state) return [];

      const levels = [];
      let items = filter.values;
      let index = 0;

      while (Array.isArray(items) && items.length) {
        const options = this.toOptions(items);
        const isSelectionLevel = options.every((option) => this.isLeaf(option));
        // на этом уровне выбор не хранится, а набирается в чипы
        const isChipLevel = isSelectionLevel && this.isMulti(filter);
        const chosen = this.isMulti(filter) ? state.path[index] : state.value;

        levels.push({
          index,
          options: isChipLevel
            ? options.filter((option) => !state.items.some((item) => item.id === option.id))
            : options,
          selected: isChipLevel ? null : (chosen ?? null),
          title: index === 0
            ? this.label(filter)
            : (this.levelTitles[filter.name] || [])[index - 1] || this.$t('shared.filters.specify'),
          isSelectionLevel,
          isChipLevel,
        });

        if (isSelectionLevel || chosen === undefined || chosen === null) break;

        const parent = options.find((option) => option.id === chosen);
        items = parent ? parent.values : null;
        index += 1;
      }

      return levels;
    },
    chipsOf(filter) {
      const state = this.state[filter.name];
      return this.isMulti(filter) && state ? state.items : [];
    },
    selectLevel(filter, level, value) {
      const state = this.state[filter.name];
      if (!state) return;

      if (!this.isMulti(filter)) {
        state.value = value ?? null;
        return;
      }

      if (level.isChipLevel) {
        const selected = level.options.find((option) => option.id === value);
        if (selected) state.items.push({ id: selected.id, name: selected.name });
        return;
      }

      // смена ветки закрывает всё, что было раскрыто под ней;
      // уже отобранные значения при этом остаются — их можно набирать из разных ветвей
      state.path = state.path.slice(0, level.index);
      if (value !== null && value !== undefined) state.path.push(value);
    },
    unselectChip(filter, id) {
      const state = this.state[filter.name];
      const index = state ? state.items.findIndex((item) => item.id === id) : -1;
      if (index >= 0) state.items.splice(index, 1);
    },
    restoreState() {
      const state = {};
      this.filterList.forEach((filter) => {
        const saved = this.selectedFilters[filter.name];
        if (this.isMulti(filter)) {
          state[filter.name] = {
            path: Array.isArray(saved?.path) ? [...saved.path] : [],
            items: Array.isArray(saved?.items) ? [...saved.items] : [],
          };
        } else {
          state[filter.name] = { value: saved?.value ?? null };
        }
      });
      this.state = state;
    },
    returnFilters() {
      const RESPONSE = {
        filtersState: {},
        filters: {},
        search: this.search || null,
        doFilter: false,
      };

      if (RESPONSE.search) RESPONSE.doFilter = true;

      this.filterList.forEach((filter) => {
        const state = this.state[filter.name];
        if (!state) return;

        if (this.isMulti(filter)) {
          RESPONSE.filtersState[filter.name] = { path: [...state.path], items: [...state.items] };
          if (state.items.length) {
            RESPONSE.filters[filter.name] = state.items.map((item) => item.id);
            RESPONSE.doFilter = true;
          }
        } else {
          RESPONSE.filtersState[filter.name] = { value: state.value };
          if (state.value !== null) {
            RESPONSE.filters[filter.name] = state.value;
            RESPONSE.doFilter = true;
          }
        }
      });

      this.$emit('update', RESPONSE);
    },
    resetFilters() {
      this.$emit('update', null);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.filters {
  &__inner {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    // панель начинается сразу под шапкой — нужен такой же отступ,
    // как у списка каталога
    padding-top: 12px;
  }
}
// поле поиска само отступов не имеет, а выпадающий список и кнопки внизу
// сидят в 8px — без этого ряды панели не выравниваются друг с другом
.search-row {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 6px 8px;
}
.btn-row, .section-btn-row {
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 6px 8px;
  gap: 10px;
}
.btn-row {
  & > button:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  & > button:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}
.section-btn-row {
  flex-wrap: wrap;
}
.up-btn-section {
  // названия разделов бывают в строку и длиннее экрана: чип переносит текст
  // и растёт в высоту, а не уезжает за край
  flex: 0 1 auto;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  background-color: $color-accent;
  border-radius: 18px;
  border: none;
  padding: 6px 20px;
  min-height: 36px;
  height: auto;
  color: $color-base;

  &__label {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  & > i {
    flex: 0 0 auto;
    margin-right: -10px;
  }
}
</style>
<!-- не scoped: этой же кнопкой каталог открывает панель фильтров -->
<style lang="scss">
@import '@/assets/style/variables.scss';

.up-btn-filters {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: $color-base;
  border: 2px solid $color-primary;
  border-radius: 18px;
  color: $color-primary;
  padding: 0 20px;
  height: 36px;
  min-height: auto;

  &:disabled {
    border-color: $color-disabled;
    color: $color-disabled;
  }
}
</style>
